import { useMemo, useState } from 'preact/hooks';
import { useBooks, useStore } from '../hooks';
import { Cover } from '../components/Cover';
import { Avatar, Empty, Stars } from '../components/ui';

// Etapa 1: jurnal de bază (pe noptieră, citite pe ani, obiectiv anual). Etapa 4 îl extinde.
export function Journal({ onOpen }: { onOpen: (id: string) => void }) {
  const s = useStore();
  const books = useBooks();
  const members = [...s.view.members.values()].sort((a, b) => a.name.localeCompare(b.name, 'ro'));
  const [who, setWho] = useState<string | null>(null);
  const memberId = who || s.me?.id || '';
  const member = s.view.members.get(memberId);
  const year = new Date().getFullYear();

  const { reading, byYear, noYear, readThisYear } = useMemo(() => {
    const rs = books.map(b => ({ b, r: b.readers.find(r => r.member_id === memberId) })).filter(x => x.r);
    const reading = rs.filter(x => x.r!.status === 'in_curs');
    const done = rs.filter(x => x.r!.status === 'citit');
    const byYear = new Map<number, typeof done>();
    const noYear: typeof done = [];
    for (const x of done) {
      const y = x.r!.read_year || (x.r!.finished_at ? Number(x.r!.finished_at.slice(0, 4)) : null);
      if (!y) { noYear.push(x); continue; }
      const arr = byYear.get(y) || []; arr.push(x); byYear.set(y, arr);
    }
    return { reading, byYear: [...byYear.entries()].sort((a, b) => b[0] - a[0]), noYear, readThisYear: byYear.get(year)?.length || 0 };
  }, [books, memberId]);

  const goal = member?.reading_goal || 25;
  const pct = Math.min(100, Math.round((readThisYear / goal) * 100));

  return (
    <div class="px-4 pb-28 pt-3 space-y-6">
      <div class="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        {members.map(m => (
          <button key={m.id} class={m.id === memberId ? 'chip-on' : 'chip-off'} onClick={() => setWho(m.id)}><Avatar member={m} size={20} /> {m.name}</button>
        ))}
      </div>

      <section class="card p-4">
        <div class="flex items-baseline justify-between">
          <div class="font-serif text-lg font-bold">Provocarea {year}</div>
          <div class="text-sm text-ink-500"><b class="text-ink-900 text-lg">{readThisYear}</b> / {goal} cărți</div>
        </div>
        <div class="mt-3 h-3 rounded-full bg-cream-200 overflow-hidden"><div class="h-full rounded-full bg-gradient-to-r from-terra-500 to-amber-500" style={{ width: `${pct}%` }} /></div>
        <div class="mt-2 text-xs text-ink-500">{pct >= 100 ? 'Obiectiv atins! 🎉' : `${goal - readThisYear} cărți până la obiectiv`}</div>
      </section>

      <section>
        <h3 class="font-serif text-lg font-bold mb-2">Pe noptieră</h3>
        {reading.length === 0 ? <p class="text-sm text-ink-500">Nicio carte în curs.</p> : (
          <ul class="space-y-2">{reading.map(({ b, r }) => (
            <li key={b.work.id}><button class="card w-full p-3 flex gap-3 text-left" onClick={() => onOpen(b.work.id)}>
              <Cover work={b.work} size="sm" />
              <div class="min-w-0"><div class="font-semibold line-clamp-1">{b.work.title}</div><div class="text-sm text-ink-500">{b.work.authors}</div>
                {r!.started_at && <div class="text-xs text-ink-500 mt-1">din {r!.started_at}</div>}</div>
            </button></li>))}
          </ul>
        )}
      </section>

      {byYear.length === 0 && noYear.length === 0 && reading.length === 0 && <Empty title="Jurnalul e gol">Marchează o carte ca „Citită” din fișa ei.</Empty>}

      {byYear.map(([y, items]) => (
        <section key={y}>
          <h3 class="font-serif text-lg font-bold mb-2">{y} <span class="text-sm font-sans font-normal text-ink-500">· {items.length} {items.length === 1 ? 'carte' : 'cărți'}</span></h3>
          <div class="grid grid-cols-4 sm:grid-cols-6 gap-3">
            {items.map(({ b, r }) => (
              <button key={b.work.id} class="text-left" onClick={() => onOpen(b.work.id)}>
                <Cover work={b.work} />
                {r!.rating ? <div class="mt-1"><Stars value={r!.rating} size="text-xs" /></div> : null}
              </button>
            ))}
          </div>
        </section>
      ))}
      {noYear.length > 0 && (
        <section>
          <h3 class="font-serif text-lg font-bold mb-2">Citite (an necunoscut) <span class="text-sm font-sans font-normal text-ink-500">· {noYear.length}</span></h3>
          <div class="grid grid-cols-4 sm:grid-cols-6 gap-3">{noYear.map(({ b }) => <button key={b.work.id} onClick={() => onOpen(b.work.id)}><Cover work={b.work} /></button>)}</div>
        </section>
      )}
    </div>
  );
}
